/**
 * Base agent config writer interface and utilities
 */

import { mkdirSync, cpSync, existsSync, readFileSync, writeFileSync, rmSync } from "fs";
import { fileURLToPath } from "url";
import { join, dirname } from "path";
import { homedir } from "os";
import { SKILL_DIR_NAME, CROSS_CLIENT_SKILLS_DIR, MCP_SERVER_NAME, MCP_SERVE_COMMAND, MCP_SERVE_ARGS } from "../constants.js";
import { getCredentialsPath } from "../auth/credentials.js";

export interface AgentConfigWriter {
  id: string;
  name: string;
  installSkill(global: boolean): void;
  installMcp(): void;
  uninstallSkill(): void;
  uninstallMcp(): void;
}

/**
 * Walk upward from `start` looking for a directory containing package.json.
 * Robust to tsup's bundling layout (chunks at dist/, entries at dist/bin|cli/).
 */
function findPackageRoot(start: string): string {
  let dir = start;
  while (true) {
    if (existsSync(join(dir, "package.json"))) return dir;
    const parent = dirname(dir);
    if (parent === dir) {
      throw new Error(`Cannot locate package.json from ${start}`);
    }
    dir = parent;
  }
}

/**
 * Get the path to the bundled SKILL.md inside this package.
 */
export function getSkillSourceDir(): string {
  const packageRoot = findPackageRoot(dirname(fileURLToPath(import.meta.url)));
  const paths = [
    join(packageRoot, "skills", SKILL_DIR_NAME),
    join(packageRoot, "src", "skill"),
  ];
  for (const p of paths) {
    if (existsSync(join(p, "SKILL.md"))) return p;
  }
  throw new Error("Cannot find bundled SKILL.md. Package may be corrupted.");
}

/**
 * Install skill to a target directory.
 */
export function copySkillTo(targetDir: string): void {
  const sourceDir = getSkillSourceDir();
  const destDir = join(targetDir, SKILL_DIR_NAME);
  mkdirSync(destDir, { recursive: true });
  cpSync(sourceDir, destDir, { recursive: true });
}

/**
 * Remove skill from a target directory.
 */
export function removeSkillFrom(targetDir: string): boolean {
  const destDir = join(targetDir, SKILL_DIR_NAME);
  if (existsSync(destDir)) {
    rmSync(destDir, { recursive: true });
    return true;
  }
  return false;
}

/**
 * Install skill to the cross-client .agents/skills/ directory.
 */
export function installCrossClientSkill(global: boolean): void {
  const baseDir = global ? join(homedir(), CROSS_CLIENT_SKILLS_DIR) : CROSS_CLIENT_SKILLS_DIR;
  copySkillTo(baseDir);
}

/**
 * Remove skill from the cross-client directory.
 */
export function removeCrossClientSkill(global: boolean): boolean {
  const baseDir = global ? join(homedir(), CROSS_CLIENT_SKILLS_DIR) : CROSS_CLIENT_SKILLS_DIR;
  return removeSkillFrom(baseDir);
}

/**
 * Build the standard MCP server entry for agent configs.
 */
export function buildMcpEntry(): { command: string; args: string[]; env: Record<string, string> } {
  const env: Record<string, string> = {
    SAILFISH_JWT_FILE: getCredentialsPath(),
  };

  // Pass through SSH_AUTH_SOCK for git clone operations
  if (process.env.SSH_AUTH_SOCK) {
    env.SSH_AUTH_SOCK = process.env.SSH_AUTH_SOCK;
  }

  return {
    command: MCP_SERVE_COMMAND,
    args: MCP_SERVE_ARGS,
    env,
  };
}

/**
 * Read a JSON file, apply a mutation, write back. Handles missing files.
 */
export function mutateJsonFile(
  filePath: string,
  mutator: (obj: any) => any,
  createIfMissing = true
): void {
  let obj: any = {};
  if (existsSync(filePath)) {
    try {
      obj = JSON.parse(readFileSync(filePath, "utf-8"));
    } catch {
      obj = {};
    }
  } else if (!createIfMissing) {
    return;
  }

  obj = mutator(obj);
  mkdirSync(dirname(filePath), { recursive: true });
  writeFileSync(filePath, JSON.stringify(obj, null, 2) + "\n");
}
